import type { MouseEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import {
  $applyNodeReplacement,
  $getNodeByKey,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DecoratorNode,
  DOMConversionOutput,
  DOMExportOutput,
  DRAGSTART_COMMAND,
  EditorConfig,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import downloadFile from '@lyearn/core/utils/network/downloadFile';
import { DownloadOutline, EyeOutline } from '@lyearn/icons';
import { IconButton, Stack } from '@lyearn/ui';

export type SerializedImageNode = Spread<
  {
    src: string;
    alt: string;
    type: 'image';
    version: 1;
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __alt: string;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__alt, node.__key);
  }

  constructor(src: string, alt: string, key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__alt = alt;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement('div');
    const theme = config.theme;
    const className = theme.image;
    if (className) {
      div.className = className;
    }
    return div;
  }

  updateDOM(): false {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.src = this.__src;
    element.alt = this.__alt;
    return { element };
  }

  setSource(src: string): void {
    const writable = this.getWritable();
    writable.__src = src;
  }

  decorate(): JSX.Element {
    return <ImageComponent alt={this.__alt} nodeKey={this.getKey()} src={this.__src} />;
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const node = $createImageNode(serializedNode.src, serializedNode.alt);
    return node;
  }

  exportJSON(): SerializedImageNode {
    return {
      ...super.exportJSON(),
      src: this.__src,
      alt: this.__alt,
      type: 'image',
      version: 1,
    };
  }
}

export function $createImageNode(src: string, alt: string): ImageNode {
  return $applyNodeReplacement(new ImageNode(src, alt));
}

export function $convertImageElement(domNode: Node): null | DOMConversionOutput {
  const img = domNode as HTMLImageElement;
  if (img.src.startsWith('file:///')) {
    return null;
  }
  const { alt, src } = img;
  const node = $createImageNode(src, alt);
  return { node };
}

function ImageComponent({ src, alt, nodeKey }: { src: string; alt: string; nodeKey: string }) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const imageRef = useRef<HTMLImageElement>(null);

  const handleDownload = useCallback(async () => {
    let filename = 'download.png';
    try {
      const urlObj = new URL(src);
      const decodedPath = decodeURIComponent(urlObj.pathname);
      const pathParts = decodedPath.split('/');
      const potentialName = pathParts[pathParts.length - 1];
      if (potentialName) {
        filename = potentialName;
      }
    } catch (e) {
      // src is likely a data URL, default filename is fine
    }
    downloadFile(src, {
      fileName: filename,
    });
  }, [src]);

  const handleView = useCallback(() => {
    if (src.startsWith('data:')) {
      fetch(src)
        .then((res) => res.blob())
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
        })
        .catch((error) => {
          console.error('Error creating object URL for image:', error);
          window.open(src, '_blank');
        });
    } else {
      window.open(src, '_blank');
    }
  }, [src]);

  const onDelete = useCallback(
    (event: KeyboardEvent) => {
      if (isSelected) {
        event.preventDefault();
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          if (node) {
            node.remove();
          }
        });
        return true;
      }
      return false;
    },
    [editor, isSelected, nodeKey],
  );

  const onClick = useCallback(
    (event: MouseEvent) => {
      if (event.target === imageRef.current) {
        event.preventDefault();
        if (event.shiftKey) {
          setSelected(!isSelected);
        } else {
          clearSelection();
          setSelected(true);
        }
        return true;
      }
      return false;
    },
    [clearSelection, setSelected, isSelected],
  );

  const onDragStart = useCallback((event: DragEvent) => {
    if (event.target === imageRef.current) {
      event.preventDefault();
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    const unregister = mergeRegister(
      editor.registerCommand(CLICK_COMMAND, onClick, COMMAND_PRIORITY_LOW),
      editor.registerCommand(DRAGSTART_COMMAND, onDragStart, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
    );

    return unregister;
  }, [editor, onClick, onDelete, onDragStart]);

  return (
    <div className="relative w-full my-8 flex justify-center">
      <div
        aria-label="Image"
        className={`w-[60%] max-w-3xl relative cursor-pointer shadow-01 hover:shadow-02 ${
          isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : ''
        }`}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick(e as unknown as MouseEvent);
          }
        }}>
        <img
          ref={imageRef}
          alt={alt}
          className="w-full rounded-lg object-cover"
          draggable="false"
          src={src}
        />
        {isSelected && (
          <Stack className="rounded-4 absolute top-2 right-2 flex space-x-2 bg-white p-[1px]">
            <IconButton onClick={handleDownload}>
              <DownloadOutline />
            </IconButton>
            <IconButton onClick={handleView}>
              <EyeOutline />
            </IconButton>
          </Stack>
        )}
      </div>
    </div>
  );
}
